if (!customElements.get('video-player')) {
  class VideoPlayer extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    autoplayObserver = null;

    constructor() {
      super();
      this.playButtonWrap = this.querySelector('[data-video-trigger-wrap]');
      this.playButton = this.querySelector('[data-video-trigger]');
      this.soundButton = this.querySelector('[data-video-sound]');
      this.fullScreenButton = this.querySelector('[data-video-fullscreen]');
      this.videoDesktop = this.querySelector('[data-video-desktop-target]');
      this.videoMobile = this.querySelector('[data-video-mobile-target]');
      this.seekBar = this.querySelector('.custom-seekbar');
      this.state = false;
      let viewportWidth = window.innerWidth;
      let that = this;

      if (viewportWidth <= 767 && this.videoMobile) {
        // Load mobile video
        this.video = this.videoMobile;
      } else {
        // Load desktop video
        this.video = this.videoDesktop;
      }

      // when resized, update video source
      window.addEventListener('resize', function (event) {
        if (window.innerWidth <= 767 && that.videoMobile) {
          // Load mobile video
          that.video = that.videoMobile;
        } else {
          // Load desktop video
          that.video = that.videoDesktop;
        }
      });

      this.loadVideo();
      this.setupViewportAutoplay();

      // Play/pause toggle on click
      if (this.playButton && this.playButton !== null) {
        this.playButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.handleVideo();
        });
      }

      // Sound toggle click
      if (this.soundButton && this.soundButton !== null) {
        this.soundButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.handleSound();
        });
      }

      // Check if video has autoplay and playsinline attributes
      if (this.video.hasAttribute('autoplay') && this.video.hasAttribute('playsinline')) {
        // Make pause button visible by default
        this.playButtonWrap.classList.add('play-button--active');
        this.state = true;
      }

      // Add event listener for pause event
      if (this.video && this.video !== null) {
        this.video.addEventListener('pause', () => {
          if (!this.playButtonWrap.classList.contains('play-button--always-active')) {
            this.state = false;
            this.playButtonWrap.classList.remove('play-button--active');
          }
        });
      }

      // Add event listener for fullscreen
      if (this.fullScreenButton && this.fullScreenButton !== null) {
        this.fullScreenButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          if (this.video.webkitEnterFullScreen) {
            this.video.webkitEnterFullScreen();
          } else {
            this.video.requestFullscreen();
          }
        });
      }

      // Add event listener for seekbar
      if (this.seekBar && this.seekBar !== null) {
        this.seekBar.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          let offset = event.offsetX;
          let totalWidth = window.innerWidth;
          let percentage = offset / totalWidth;
          let vidTime = this.video.duration * percentage;
          this.video.currentTime = vidTime;
        });
      }

      // Add video time update
      if (this.video && this.video !== null) {
        this.video.ontimeupdate = function (event) {
          let percentage = (event.target.currentTime / event.target.duration) * 100;
          if (
            this.closest('video-player').querySelector('.custom-seekbar span') &&
            this.closest('video-player').querySelector('.custom-seekbar span') !== null
          ) {
            this.closest('video-player')
              .querySelector('.custom-seekbar span')
              .classList.add('some');
            this.closest('video-player').querySelector('.custom-seekbar span').style.width =
              percentage + '%';
          }
        };
      }
    }

    disconnectedCallback() {
      if (this.autoplayObserver) {
        this.autoplayObserver.disconnect();
        this.autoplayObserver = null;
      }
    }

    /**
     * Play/pause autoplay videos when they enter/leave the viewport (per video-observer pattern).
     * Only runs for elements matching `video[autoplay]`.
     * Optional JSON on `data-options` (e.g. `{"threshold":0.25}`) merges with defaults.
     */
    setupViewportAutoplay() {
      const autoplayVideos = this.querySelectorAll('video[autoplay]');
      if (!autoplayVideos.length) return;

      if (!('IntersectionObserver' in window)) {
        autoplayVideos.forEach((video) => {
          video.play().catch(() => {});
        });
        return;
      }

      let observerOptions = { threshold: 0.25 };
      try {
        const parsed = JSON.parse(this.dataset.options || '{}');
        observerOptions = { ...observerOptions, ...parsed };
      } catch (_) {
        /* ignore invalid JSON */
      }

      this.autoplayObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, observerOptions);

      autoplayVideos.forEach((video) => this.autoplayObserver.observe(video));
    }

    /**
     * Handle sound interaction
     */
    handleSound() {
      this.video.muted = !this.video.muted;
      this.playButtonWrap.classList.toggle('has-sound');
    }

    /**
     * Load desktop or mobile video based on viewport width
     */
    loadVideo() {
      let viewportWidth = window.innerWidth;

      if (viewportWidth <= 767 && this.videoMobile) {
        // Load mobile video
        this.video = this.videoMobile;
      } else {
        // Load desktop video
        this.video = this.videoDesktop;
      }

      this.video.load();
    }

    /**
     * Handle video interaction
     */
    handleVideo() {
      this.state = !this.state;

      if (!this.playButtonWrap.classList.contains('play-button--always-active')) {
        this.state
          ? this.playButtonWrap.classList.add('play-button--active')
          : this.playButtonWrap.classList.remove('play-button--active');
      } else {
        this.state
          ? this.playButtonWrap.classList.remove('play-button--new-active')
          : this.playButtonWrap.classList.add('play-button--new-active');
      }
      // Loop sources
      this.querySelectorAll('source').forEach((source) => {
        source.src = source.dataset.src;
      });
      // Play or pause
      if (this.state) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }
  }
  customElements.define('video-player', VideoPlayer);
}
